import { useMemo, useState } from 'react';
import cn from 'clsx';
import { Link, useLocation } from 'react-router-dom';
import type { EventKol } from 'r/atoms';
import { SpinnerCircle } from 'r/ui';
import { dayjs } from 'r/utils';
import { Button } from '@/components/ui/button';
import PaginationControls from '../../AnalysisManagerTab/PaginationControls';
import { DirectionBadge } from '../shared';
import { useKolAnalyses } from './queries';
import useOpenPlayEvent from './useOpenPlayEvent';

export default function KolAnalyses({ kol }: { kol: EventKol }) {
  const location = useLocation();
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const { openPlayEvent, isPending } = useOpenPlayEvent();

  const analysesQuery = useKolAnalyses(kol.kolID, page, limit);
  const analyses = analysesQuery.data?.data ?? [];
  const total = analysesQuery.data?.total ?? 0;

  const eventsByTweet = useMemo(
    () => new Map(kol.events.map((e) => [e.tweetID, e])),
    [kol.events],
  );

  if (analysesQuery.isLoading) {
    return (
      <div className="flex justify-center p-10">
        <SpinnerCircle size={24} color="var(--muted-foreground)" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="text-sm text-secondary font-medium">
        {total} analyses in CMS
        {analysesQuery.isFetching && (
          <span className="ml-2 text-blue-500 animate-pulse">Updating...</span>
        )}
      </div>

      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-left text-sm">
          <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
            <tr>
              <th className="py-3 px-4 font-medium">Tweet date</th>
              <th className="py-3 px-4 font-medium">Tweet</th>
              <th className="py-3 px-4 font-medium">Pair</th>
              <th className="py-3 px-4 font-medium">Direction</th>
              <th className="py-3 px-4 font-medium">Status</th>
              <th className="py-3 px-4 font-medium" />
            </tr>
          </thead>
          <tbody>
            {analyses.map((analysis) => {
              const event = eventsByTweet.get(analysis.tweetID);
              return (
                <tr
                  key={analysis.id}
                  className={cn(
                    'border-b hover:bg-muted/50 transition-colors',
                    event && 'bg-purple-500/5',
                  )}
                >
                  <td className="py-3 px-4 text-muted-foreground whitespace-nowrap">
                    {dayjs(analysis.tweetDate).format('DD.MM.YYYY HH:mm')}
                  </td>
                  <td className="py-3 px-4 max-w-md">
                    <a
                      href={`https://x.com/${analysis.authorUsername}/status/${analysis.tweetID}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="line-clamp-2 hover:underline"
                      title={analysis.tweetText}
                    >
                      {analysis.tweetText}
                    </a>
                  </td>
                  <td className="py-3 px-4 font-semibold whitespace-nowrap">
                    {analysis.pair}
                  </td>
                  <td className="py-3 px-4">
                    <DirectionBadge isUp={analysis.isPredictionUp} />
                  </td>
                  <td className="py-3 px-4 text-xs whitespace-nowrap">
                    {event ?
                      <span className="text-purple-500 font-medium">Event</span>
                    : analysis.verifiedBy ?
                      <span className="text-yellow-600">Verified</span>
                    : <span className="text-secondary">—</span>}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" asChild>
                        <Link
                          to={`/analyses/${analysis.id}/edit`}
                          state={{ background: location }}
                        >
                          Edit
                        </Link>
                      </Button>
                      {event ?
                        <Button variant="outline" size="sm" asChild>
                          <Link
                            to={`/events/${event.id}/edit`}
                            state={{ background: location }}
                          >
                            Open event
                          </Link>
                        </Button>
                      : <Button
                          size="sm"
                          onClick={() => openPlayEvent(analysis)}
                          disabled={isPending}
                        >
                          Create event
                        </Button>
                      }
                    </div>
                  </td>
                </tr>
              );
            })}
            {analyses.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="py-8 text-center text-muted-foreground"
                >
                  No analyses for this author
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <PaginationControls
        key={page}
        page={page}
        total={total}
        limit={limit}
        limitOptions={[20, 50, 100]}
        onPageChange={setPage}
        onLimitChange={(value) => {
          setLimit(value);
          setPage(0);
        }}
      />
    </div>
  );
}
